/**
 * Shared widget-preference plumbing.
 *
 * The chosen MatchWidget look + customization (see `WidgetPrefs` in
 * ./MatchWidget) is persisted in AsyncStorage so BOTH sides agree:
 *  - the in-app picker (src/app/widgets.tsx) WRITES it, then asks the live tile
 *    to redraw via requestWidgetUpdate;
 *  - the headless widget task handler (./taskHandler) READS it before rendering.
 *
 * AsyncStorage is safe to import from the headless task (it's a plain native
 * key/value store, no React state). Everything here is defensive: a missing or
 * junk value resolves to the defaults, never throws.
 *
 * STORAGE SHAPE. The full preference bundle (style + accent + layout +
 * favoriteFirst) lives as ONE JSON blob under `WIDGET_PREFS_KEY`. The original
 * single-value `WIDGET_STYLE_KEY` is kept and written in lock-step so any older
 * code path (or a partial rollout) still reads a valid style. On read, the blob
 * wins; if it is absent we fall back to the legacy style key, then to defaults.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

import {
  DEFAULT_WIDGET_PREFS,
  DEFAULT_WIDGET_STYLE,
  normalizeWidgetPrefs,
  normalizeWidgetStyle,
  type WidgetPrefs,
  type WidgetStyle,
} from './MatchWidget';

/** AsyncStorage key for the legacy single-value widget style (kept in sync). */
export const WIDGET_STYLE_KEY = 'tashkeela.widgetStyle';
/** AsyncStorage key for the full widget preference bundle (JSON). */
export const WIDGET_PREFS_KEY = 'tashkeela.widgetPrefs';

/**
 * Read the full persisted prefs. Never throws — any read/parse error or
 * missing/unknown values collapse (field-by-field) to the defaults. Falls back
 * to the legacy style-only key when the bundle is absent so an upgrade from the
 * old single-value storage keeps the user's chosen style.
 */
export async function getWidgetPrefs(): Promise<WidgetPrefs> {
  try {
    const raw = await AsyncStorage.getItem(WIDGET_PREFS_KEY);
    if (raw) {
      // JSON.parse can throw on junk — normalizeWidgetPrefs handles the parsed
      // object, but guard the parse itself.
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = null;
      }
      if (parsed) return normalizeWidgetPrefs(parsed);
    }
    // No bundle yet: seed from the legacy style key (defaults for the rest).
    const legacyStyle = normalizeWidgetStyle(await AsyncStorage.getItem(WIDGET_STYLE_KEY));
    return { ...DEFAULT_WIDGET_PREFS, style: legacyStyle };
  } catch {
    return DEFAULT_WIDGET_PREFS;
  }
}

/**
 * Persist the full prefs bundle (and keep the legacy style key in sync). Returns
 * false (never throws) on failure. Input is normalized so a partial/junk object
 * can never corrupt storage.
 */
export async function setWidgetPrefs(prefs: WidgetPrefs): Promise<boolean> {
  const next = normalizeWidgetPrefs(prefs);
  try {
    await AsyncStorage.multiSet([
      [WIDGET_PREFS_KEY, JSON.stringify(next)],
      [WIDGET_STYLE_KEY, next.style],
    ]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Read just the persisted widget style. Kept for back-compat with any caller
 * that only needs the style. Never throws — missing/junk → default `gradient`.
 */
export async function getWidgetStyle(): Promise<WidgetStyle> {
  try {
    return (await getWidgetPrefs()).style;
  } catch {
    return DEFAULT_WIDGET_STYLE;
  }
}

/**
 * Persist just the chosen style (merging into the existing prefs bundle so the
 * other customization fields are preserved). Returns false (never throws) on
 * failure.
 */
export async function setWidgetStyle(style: WidgetStyle): Promise<boolean> {
  try {
    const current = await getWidgetPrefs();
    return await setWidgetPrefs({ ...current, style: normalizeWidgetStyle(style) });
  } catch {
    return false;
  }
}
